import type { AiqDiagnosticReportResponse } from '@/api/aiq'
import type { AiqDomain, MathBand } from '@/constants/aiq'
import { courseDisplayName } from '@/utils/courseName'
import type { CourseNameLabels } from '@/utils/courseName'
import type {
  CumulativeAssessmentReportView,
  CumulativeReportBand,
  CumulativeReportChapterView,
  CumulativeReportCompetencyView,
  CumulativeReportMathHeadView,
} from '@/types/coaching'

const KST = 'Asia/Seoul'

// 과정 이름 규칙은 학습자 화면과 공유한다(`@/utils/courseName`). 리포트 쪽 import 경로는 유지한다.
export { courseDisplayName }
export type { CourseNameLabels }

/**
 * Competency narrative bands (D16), relative to the World baselines frozen with the snapshot and printed beside
 * each bar: at or above the World's top-30% line is `high`; below the same distance under the World mean is
 * `low` (the top-30% line mirrored, about the bottom 30% for a symmetric distribution); anything between is
 * `mid`. Because both baselines are frozen, a frozen score always prints the same prose (ADR 0012), and the text
 * can never contradict the bar chart. Decided 2026-09-19 after staging showed World means near 40 on the display
 * scale, where absolute cut-offs would almost never reach `high`.
 *
 * `MIN_BAND_HALF_WIDTH` keeps a World whose baselines sit almost together from flipping high/low on one point.
 */
export const COMPETENCY_BANDS = {
  MIN_BAND_HALF_WIDTH: 3,
} as const

export function competencyBandOf(competency: CumulativeReportCompetencyView): MathBand {
  const mean = competency.populationMean0to100
  const halfWidth = Math.max(competency.top30Percent0to100 - mean, COMPETENCY_BANDS.MIN_BAND_HALF_WIDTH)
  if (competency.score0to100 >= mean + halfWidth) return 'high'
  if (competency.score0to100 < mean - halfWidth) return 'low'
  return 'mid'
}

export const COMPETENCY_DOMAIN: Record<CumulativeReportCompetencyView['key'], AiqDomain> = {
  A: 'abstraction',
  C: 'change',
  R: 'relation',
}

/**
 * Index-scale baselines of the Math Head page-4 comparison. The Math Head Index is `100 + 15z` against the
 * World's composite norm and BIGGO reads its percentile from the N(100, 15) tail, so on that same scale the
 * World's general learner sits at 100 and its 70th percentile at 100 + 15 × 0.5244 ≈ 108.
 */
export const MATH_HEAD_POPULATION_INDEX = 100
export const MATH_HEAD_TOP30_INDEX = 108

function kstParts(iso: string): Record<string, string> {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return {}
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: KST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  return Object.fromEntries(parts.map((part) => [part.type, part.value]))
}

/**
 * 완료일시 — 리포트가 인쇄하는 시각. 발행(`publishedAt`)은 동결돼 있어 총평을 고치거나 수학머리 스냅샷이 채워진
 * 뒤 다시 인쇄해도 날짜가 그대로였다(아사나 1218732821759163). 표지·머리글·파일명은 모두 이 값을 쓴다.
 * `lastUpdatedAt` 을 아직 내려주지 않는 백엔드에서는 발행 시각으로 떨어진다.
 */
export function reportStampOf(report: Pick<CumulativeAssessmentReportView, 'publishedAt' | 'lastUpdatedAt'>): string {
  return report.lastUpdatedAt ?? report.publishedAt
}

/** `2026.09.18` — every page header. */
export function formatHeaderDate(iso: string): string {
  const p = kstParts(iso)
  return p.year ? `${p.year}.${p.month}.${p.day}` : '—'
}

/** `2026. 09. 18 (목) 15:30` — the cover's 완료일시, same instant as the header date (D18). */
export function formatCoverDate(iso: string, locale: string): string {
  const p = kstParts(iso)
  if (!p.year) return '—'
  const weekday = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'ko-KR', { timeZone: KST, weekday: 'short' })
    .format(new Date(iso))
  return `${p.year}. ${p.month}. ${p.day} (${weekday}) ${p.hour}:${p.minute}`
}

function sanitize(value: string, fallback: string): string {
  return value.trim().replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '') || fallback
}

/**
 * D17 (revised 2026-09-22, Asana 1218732821759163): `{이름}_{로그인ID}_{YYMMDD_HH_MM}_{N차}_누적리포트`, stamped with
 * the KST minute of [reportStampOf] — the report's 완료일시, so a document re-printed after an edit is filed under
 * its new stamp (revised 2026-09-24). Same login-id basis as the chapter test print, plus the round the headline
 * shows ("누적 테스트 N차" = Lifetime Assessment Sequence) so a test print and its report sort side by side.
 */
export function buildCumulativeReportFileName(
  report: CumulativeAssessmentReportView,
  roundLabel: string,
  suffix: string,
): string {
  const p = kstParts(reportStampOf(report))
  const stamp = p.year ? `${p.year.slice(-2)}${p.month}${p.day}_${p.hour}_${p.minute}` : 'date-unknown'
  return [
    sanitize(report.learner.name, 'student'),
    sanitize(report.learner.loginId ?? '', 'login-id-unknown'),
    stamp,
    sanitize(roundLabel, 'round-unknown'),
    suffix,
  ].join('_')
}

/**
 * Groups chapters by course so each line reads `코어 C 챕터. 1 ~ 3`.
 * 번호는 과정 안에서 1부터 센 것이다 — 전역 번호를 쓰면 16챕터짜리 어드밴스드의 첫 챕터가 "챕터 19"로 읽힌다.
 */
export function groupChaptersByCourse(chapters: CumulativeReportChapterView[]) {
  const groups: { courseTitle: string | null; ordinals: number[] }[] = []
  chapters.forEach((chapter) => {
    const courseTitle = chapter.courseTitle ?? null
    const number = chapter.chapterNumberInCourse ?? chapter.chapterOrdinal
    const last = groups.at(-1)
    if (last && last.courseTitle === courseTitle) last.ordinals.push(number)
    else groups.push({ courseTitle, ordinals: [number] })
  })
  return groups
}

/** Adapts the frozen Math Head snapshot to the diagnostic AIQ pages it reuses (F6). */
export function toAiqReportData(
  report: CumulativeAssessmentReportView,
  mathHead: CumulativeReportMathHeadView,
): AiqDiagnosticReportResponse {
  return {
    nickName: report.learner.name,
    ageLabel: '',
    gradeLabel: report.learner.gradeLabel,
    diagnosedAt: reportStampOf(report),
    aiqScore: mathHead.index,
    aiqRank: mathHead.percentile,
    sameAgeAiqScore: MATH_HEAD_POPULATION_INDEX,
    quebonAiqScore: MATH_HEAD_TOP30_INDEX,
    domainScores: mathHead.competencies.map((competency) => {
      const domain = COMPETENCY_DOMAIN[competency.key]
      return {
        domain,
        score: competency.score0to100,
        sameAgeScore: competency.populationMean0to100,
        quebonScore: competency.top30Percent0to100,
        content: competency.content ?? '',
      }
    }),
  }
}

/** The same report with every course title renamed through [courseDisplayName]. */
export function withCourseDisplayNames(
  report: CumulativeAssessmentReportView,
  labels: CourseNameLabels,
): CumulativeAssessmentReportView {
  const chapter = (view: CumulativeReportChapterView): CumulativeReportChapterView =>
    view.courseTitle ? { ...view, courseTitle: courseDisplayName(view.courseTitle, labels) } : view
  return {
    ...report,
    scope: report.scope.map(chapter),
    reviewRange: report.reviewRange.map(chapter),
    courseTrack: report.courseTrack?.map((course) => ({ ...course, title: courseDisplayName(course.title, labels) })),
  }
}

/**
 * i18n key of the default Cumulative Overall Comment for a Math Head band × Lesson Test band pair, or null when
 * either result is missing (exempted Lesson Test, Math Head below norm sufficiency) — then no default is offered.
 */
export function defaultOverallCommentKey(
  lessonBand: CumulativeReportBand | null | undefined,
  mathHeadBand: CumulativeReportBand | null | undefined,
): string | null {
  if (!lessonBand || !mathHeadBand) return null
  return `cumulativeReport.defaultComment.mathHead${BAND_KEY[mathHeadBand]}Lesson${BAND_KEY[lessonBand]}`
}

const BAND_KEY: Record<CumulativeReportBand, string> = { HIGH: 'High', MID: 'Mid', LOW: 'Low' }
